<script setup lang="ts">
import { ref, onMounted, onUnmounted } from 'vue';
import { EXPLOSION_CLASS_COLORS } from '@/types/geolocation';
import type { LiveDetection, DetectionTracePoint } from './useLiveDetection';

/**
 * Tiny detection sparkline for the header, next to the mic button. A *very* simplified, slowly
 * drifting view of detector activity over the last hour: each pixel column is one short time
 * bucket, its bar height is the peak confidence in that bucket, coloured by class when a
 * detection actually fired (dim otherwise). Newest activity is on the right.
 *
 * Because the window is an hour wide, it crawls — one bucket only leaves the strip after minutes.
 * It reads the same trace as the detailed chart but redraws lazily (no rAF), so it's cheap.
 */

const props = defineProps<{ detection: LiveDetection }>();

const WINDOW_MS = 60 * 60 * 1000; // 1 hour
const HEIGHT = 30;
const DIM = '#3a4660';

const canvas = ref<HTMLCanvasElement | null>(null);
let ctx: CanvasRenderingContext2D | null = null;
let unsubscribe: (() => void) | null = null;
let timer: ReturnType<typeof setInterval> | null = null;
let points: DetectionTracePoint[] = [];

function classColor(c: DetectionTracePoint['className']): string {
  return c ? EXPLOSION_CLASS_COLORS[c] : DIM;
}

function resize(): void {
  if (!canvas.value) return;
  const dpr = window.devicePixelRatio || 1;
  const cssW = canvas.value.clientWidth || 84;
  canvas.value.width = Math.round(cssW * dpr);
  canvas.value.height = Math.round(HEIGHT * dpr);
  ctx?.setTransform(dpr, 0, 0, dpr, 0, 0);
  draw();
}

function draw(): void {
  if (!canvas.value || !ctx) return;
  const w = canvas.value.clientWidth || 84;
  const h = HEIGHT;
  ctx.clearRect(0, 0, w, h);
  ctx.fillStyle = '#0b101a';
  ctx.fillRect(0, 0, w, h);

  const now = Date.now();
  const cols = Math.max(1, Math.floor(w));
  // Per-column bucket: peak confidence + whether anything fired + the class to colour it.
  const conf = new Float32Array(cols);
  const fired = new Uint8Array(cols);
  const cls: (DetectionTracePoint['className'])[] = new Array(cols).fill(null);

  for (const p of points) {
    if (p.tMs < now - WINDOW_MS) continue;
    const col = Math.min(cols - 1, Math.floor(((p.tMs - (now - WINDOW_MS)) / WINDOW_MS) * cols));
    if (p.confidence > conf[col]) {
      conf[col] = p.confidence;
      cls[col] = p.className;
    }
    if (p.fired) {
      fired[col] = 1;
      cls[col] = p.className; // a fired window dictates the bucket colour
    }
  }

  for (let c = 0; c < cols; c++) {
    if (conf[c] <= 0 && !fired[c]) continue;
    const barH = Math.max(fired[c] ? 3 : 1, conf[c] * (h - 2));
    ctx.fillStyle = fired[c] ? classColor(cls[c]) : DIM;
    ctx.globalAlpha = fired[c] ? 1 : 0.5;
    ctx.fillRect(c, h - barH, 1, barH);
  }
  ctx.globalAlpha = 1;
}

onMounted(() => {
  ctx = canvas.value?.getContext('2d') ?? null;
  window.addEventListener('resize', resize);
  points = props.detection.getTrace();
  resize();
  unsubscribe = props.detection.onTrace((p) => {
    points.push(p);
    const cutoff = Date.now() - WINDOW_MS - 10000;
    if (points.length > 0 && points[0].tMs < cutoff) {
      points = points.filter((q) => q.tMs >= cutoff);
    }
    draw();
  });
  // Redraw slowly so the strip keeps drifting left even when no new windows arrive.
  timer = setInterval(draw, 5000);
});

onUnmounted(() => {
  window.removeEventListener('resize', resize);
  if (timer) clearInterval(timer);
  unsubscribe?.();
  unsubscribe = null;
  points = [];
});
</script>

<template>
  <div
    class="mini-chart"
    title="Активность детектора за последний час"
  >
    <canvas ref="canvas" />
  </div>
</template>

<style scoped>
.mini-chart {
  width: 84px;
  height: 30px;
  flex-shrink: 0;
  border-radius: 8px;
  overflow: hidden;
  background: #0b101a;
  border: 1px solid rgba(255, 255, 255, 0.1);
  line-height: 0;
}
.mini-chart canvas {
  width: 100%;
  height: 30px;
  display: block;
}
</style>
